import React from 'react';
import { Link } from 'react-router-dom';
import { FiTruck, FiShield, FiAward, FiClock, FiArrowRight, FiTarget, FiUsers } from 'react-icons/fi';
import { FaChartLine, FaLeaf, FaHandHoldingUsd, FaPaw } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const Home = () => {
  const features = [
    { icon: <FiTruck className="w-10 h-10" />, title: "Free Consultation", desc: "Expert guidance for your investments", color: "bg-blue-50", iconColor: "text-blue-600" },
    { icon: <FiShield className="w-10 h-10" />, title: "100% Secure", desc: "Your investments are completely safe", color: "bg-green-50", iconColor: "text-green-600" },
    { icon: <FiAward className="w-10 h-10" />, title: "Up to 72% Returns", desc: "Guaranteed returns annually", color: "bg-yellow-50", iconColor: "text-yellow-600" },
    { icon: <FiClock className="w-10 h-10" />, title: "24/7 Support", desc: "Round the clock customer service", color: "bg-purple-50", iconColor: "text-purple-600" }
  ];

  // ============ 3 PLANS: Basic, Standard, Premium ============
  const plans = [
    { name: 'Basic', min: '₹10,000', returns: '60%', duration: '1 Year', color: 'from-blue-500 to-blue-600', bgColor: 'bg-blue-50' },
    { name: 'Standard', min: '₹10 Lakh+', returns: '66%', duration: '1 Year', color: 'from-green-500 to-green-600', bgColor: 'bg-green-50' },
    { name: 'Premium', min: '₹20 Lakh+', returns: '72%', duration: '2 Years', color: 'from-purple-500 to-purple-600', bgColor: 'bg-purple-50' }
  ];

  const businessHighlights = [
    { icon: <FaPaw className="w-8 h-8" />, title: "5000+ Cows", desc: "Healthy, well-maintained Gir and Sahiwal cows" },
    { icon: <FaLeaf className="w-8 h-8" />, title: "Organic Farming", desc: "100% organic and chemical-free farming practices" },
    { icon: <FaHandHoldingUsd className="w-8 h-8" />, title: "₹250Cr+ Assets", desc: "Managing assets worth over ₹250 crores" },
    { icon: <FiUsers className="w-8 h-8" />, title: "1000+ Farmers", desc: "Partnered with local farmers across India" }
  ];

  return (
    <>
      <Helmet>
        <title>Assets Dairy Farm - India's Trusted Dairy Investment Platform</title>
        <meta name="description" content="Invest in Assets Dairy Farm and get guaranteed returns up to 72% annually. Start your investment journey today." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-700 to-green-900 text-white">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="relative container mx-auto px-4 py-24 md:py-32 z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              <span className="text-sm font-medium">Trusted by Thousands of Investors</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Invest in Assets Dairy Farm
              <span className="block text-yellow-400">Get Up to 72% Guaranteed Returns</span>
            </h1>
            <p className="text-xl mb-8 opacity-95 max-w-2xl mx-auto">
              Join India's fastest-growing dairy farm investment platform. Earn up to 72% fixed returns with complete transparency.
            </p>
            <div className="flex justify-center">
              <Link 
                to="/investor/plans" 
                className="inline-block bg-yellow-500 text-gray-900 font-bold py-4 px-10 rounded-lg hover:bg-yellow-400 transition duration-300 transform hover:scale-105 shadow-xl text-lg"
              >
                Invest Now <FiArrowRight className="inline ml-2" />
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 wave-svg">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
            <path fill="#ffffff" fillOpacity="1" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,154.7C960,171,1056,181,1152,165.3C1248,149,1344,107,1392,85.3L1440,64L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
        </div>
      </section>

      {/* About Assets Dairy Farm Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">About Assets Dairy Farm</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">India's Most Trusted and Fastest-Growing Dairy Farm Network</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1563636619-e9143da7973b?ixlib=rb-4.0.3" 
                alt="Assets Dairy Farm" 
                className="rounded-2xl shadow-xl w-full h-96 object-cover"
              />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Our Journey</h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Founded in 2015, Assets Dairy Farm started with a simple mission - to revolutionize the Indian dairy industry by connecting urban investors with sustainable, profitable farming opportunities.
              </p>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Today, we manage over 5,000+ high-yield cows across 15+ partner farms in Gujarat, Maharashtra, and Punjab. Our state-of-the-art facilities use modern technology to ensure maximum milk production while maintaining the highest standards of animal welfare.
              </p>
              <p className="text-gray-600 leading-relaxed">
                With ₹250+ crores in assets under management and 12,500+ satisfied investors, Assets Dairy Farm has become India's most trusted dairy investment platform.
              </p>
            </div>
          </div>

          {/* Business Highlights */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {businessHighlights.map((item, idx) => (
              <div key={idx} className="bg-green-50 rounded-xl p-6 text-center hover:shadow-lg transition">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white">
                  {item.icon}
                </div>
                <h4 className="text-xl font-bold text-gray-800 mb-2">{item.title}</h4>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Why Invest With Us */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-8 text-white mb-16">
            <h3 className="text-2xl font-bold text-center mb-8">Why Invest With Assets Dairy Farm?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <FiTarget className="w-12 h-12 mx-auto mb-3 text-yellow-400" />
                <h4 className="font-semibold text-lg mb-2">Up to 72% Returns</h4>
                <p className="text-sm opacity-90">Highest returns in the dairy farming sector</p>
              </div>
              <div className="text-center">
                <FaHandHoldingUsd className="w-12 h-12 mx-auto mb-3 text-yellow-400" />
                <h4 className="font-semibold text-lg mb-2">Monthly Payouts</h4>
                <p className="text-sm opacity-90">Get your profits credited every month</p>
              </div>
              <div className="text-center">
                <FiShield className="w-12 h-12 mx-auto mb-3 text-yellow-400" />
                <h4 className="font-semibold text-lg mb-2">100% Capital Protection</h4>
                <p className="text-sm opacity-90">Your principal amount is fully secured</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">12,500+</div>
              <div className="text-gray-600">Happy Investors</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">₹250Cr+</div>
              <div className="text-gray-600">Total Invested</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">72%</div>
              <div className="text-gray-600">Max Returns</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">15+</div>
              <div className="text-gray-600">Partner Farms</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Why Choose Assets Dairy Farm?</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">We make dairy farming investment simple, secure, and profitable</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className={`${feature.color} p-6 rounded-xl text-center hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1`}>
                <div className={`${feature.iconColor} mb-4 flex justify-center`}>{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Plans Preview - 3 Plans */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Investment Plans</h2>
            <p className="text-gray-600 text-lg">Choose the plan that suits your financial goals</p>
          </div>
          <div className="flex justify-center">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl w-full">
              {plans.map((plan, idx) => (
                <div key={idx} className={`${plan.bgColor} rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}>
                  <div className={`bg-gradient-to-r ${plan.color} p-4 text-white text-center`}>
                    <h3 className="text-xl font-bold">{plan.name}</h3>
                  </div>
                  <div className="p-5 text-center bg-white">
                    <p className="text-3xl font-bold text-green-600">{plan.min}</p>
                    <p className="text-gray-500 text-sm mt-1">Minimum Investment</p>
                    <p className="text-2xl font-bold text-gray-800 mt-3">{plan.returns}</p>
                    <p className="text-gray-600 text-sm">Guaranteed Returns</p>
                    <p className="text-xs text-gray-400 mt-2">Lock-in: {plan.duration}</p>
                    <Link to="/investor/plans" className="mt-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition w-full inline-block text-sm">
                      View Details →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-green-700 to-green-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <FaChartLine className="w-16 h-16 mx-auto mb-6 text-yellow-400" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Start Your Investment Journey Today</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Join thousands of smart investors who are earning up to 72% guaranteed returns with Assets Dairy Farm
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/about" className="inline-block bg-transparent border-2 border-white text-white font-bold py-3 px-8 rounded-lg hover:bg-white hover:text-green-700 transition">
              Learn More
            </Link>
            <Link to="/investor/plans" className="inline-block bg-yellow-500 text-gray-900 font-bold py-3 px-8 rounded-lg hover:bg-yellow-400 transition shadow-lg">
              Invest Now <FiArrowRight className="inline ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;